import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, Plus, X } from 'lucide-react';
import { useToast } from '../../../src/contexts/ToastContext';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import { propsFundoModal } from '../../../src/lib/backdropModal';
import { atualizarPagamento, fichaDaPessoa } from '../../../src/services/indicacoes';
import { validarDocumentoOpcional } from '../../../src/lib/indicacoes/formulario';
import {
  ANDAMENTO,
  ROTULO_CANAL,
  ROTULO_MOTIVO,
  ROTULO_RESULTADO,
  SELO_CLIENTE,
  formatarDataHora,
  resumoPessoa,
} from '../../../src/lib/indicacoes/rotulos';
import { formatarTelefoneBr } from '../../../src/lib/enderecoUc';
import type { IndicacaoFicha, IndicacaoItem } from '../../../types';
import NegotiationDetailModal from '../components/NegotiationDetails/NegotiationDetailModal';
import RegistrarAbordagemModal from './RegistrarAbordagemModal';
import Selo from './Selo';
import { CLASSE_BOTAO_PRIMARIO, CLASSE_BOTAO_SECUNDARIO, CLASSE_INPUT } from './estilos';

// z-index 90: abaixo do detalhe da negociação (z-100), que abre por cima ao clicar numa indicação.

export interface AlvoFicha {
  pessoaId: string | null;
  cpfCnpj: string | null;
}

const LinhaIndicacao: React.FC<{ i: IndicacaoItem; onAbrir: (id: number) => void }> = ({ i, onAbrir }) => {
  const est = ANDAMENTO[i.andamento];
  const negId = i.negociacao_id;
  return (
    <div className="mt-1.5 flex items-center justify-between gap-2 text-sm">
      <div className="min-w-0">
        <span className="font-medium text-gray-900 dark:text-white">→ {i.indicado_nome}</span>
        {i.indicado_whatsapp && (
          <span className="text-xs text-gray-500 dark:text-slate-400 ml-2">{formatarTelefoneBr(i.indicado_whatsapp)}</span>
        )}
        <p className="text-xs text-gray-500 dark:text-slate-400">
          {i.vale
            ? i.responsavel_nome ? `com ${i.responsavel_nome}` : 'sem responsável'
            : `não vale: ${i.motivo_nao_vale ? ROTULO_MOTIVO[i.motivo_nao_vale] : ''}`}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Selo classe={est.classe}>{est.rotulo}</Selo>
        {negId != null && (
          <button onClick={() => onAbrir(negId)} className="p-1 text-gray-400 hover:text-primary" title="Abrir negociação">
            <ExternalLink size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

interface Props {
  alvo: AlvoFicha;
  gere: boolean;
  onClose: () => void;
  onMudou: () => void;
}

const FichaPessoaDrawer: React.FC<Props> = ({ alvo, gere, onClose, onMudou }) => {
  const { addToast } = useToast();
  const [ficha, setFicha] = useState<IndicacaoFicha | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [registrar, setRegistrar] = useState(false);
  const [negAberta, setNegAberta] = useState<number | null>(null);
  const [cpf, setCpf] = useState('');
  const [pix, setPix] = useState('');
  const [salvandoPag, setSalvandoPag] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const f = await fichaDaPessoa(alvo);
      setFicha(f);
      setCpf(f.pessoa.cpf_cnpj ?? '');
      setPix(f.pessoa.chave_pix ?? '');
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [alvo.pessoaId, alvo.cpfCnpj]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const porAbordagem = useMemo(() => {
    const m = new Map<number, IndicacaoItem[]>();
    ficha?.indicacoes.forEach(i => {
      if (i.abordagem_id != null) m.set(i.abordagem_id, [...(m.get(i.abordagem_id) ?? []), i]);
    });
    return m;
  }, [ficha]);
  const espontaneas = ficha?.indicacoes.filter(i => i.forma === 'espontanea') ?? [];

  const salvarPagamento = async () => {
    if (!ficha?.pessoa.id) return;
    const v = validarDocumentoOpcional(cpf);
    if (!v.ok) {
      addToast(v.erro ?? 'CPF/CNPJ inválido.', 'warning');
      return;
    }
    setSalvandoPag(true);
    try {
      await atualizarPagamento(ficha.pessoa.id, cpf || null, pix || null);
      addToast('Dados de pagamento salvos.', 'success');
      await carregar();
      onMudou();
    } catch (e) {
      addToast((e as Error).message, 'error');
    } finally {
      setSalvandoPag(false);
    }
  };

  const p = ficha?.pessoa;
  const selo = p?.cliente ? SELO_CLIENTE.sim : SELO_CLIENTE.nao;

  return (
    <>
      <div className="fixed inset-0 z-[90] flex justify-end bg-black/40" {...propsFundoModal(onClose)}>
        <aside className="w-full max-w-xl h-full bg-white dark:bg-slate-900 shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
          <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-slate-800">
            <div className="min-w-0">
              {p && ficha ? (
                <>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white truncate">{p.nome}</h2>
                    <Selo classe={selo.classe}>{selo.rotulo}</Selo>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                    {[p.whatsapp ? formatarTelefoneBr(p.whatsapp) : null, p.ucs > 0 ? `${p.ucs} UC${p.ucs > 1 ? 's' : ''}` : null, p.cidade]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-slate-300 mt-1">
                    {resumoPessoa({
                      abordagens: ficha.abordagens.length,
                      ultima_abordagem_em: ficha.abordagens[0]?.abordado_em ?? null,
                      ultima_abordagem_por: ficha.abordagens[0]?.abordado_por_nome ?? null,
                      indicacoes: ficha.indicacoes.length,
                      aprovadas: ficha.indicacoes.filter(i => i.andamento === 'aprovada' || i.andamento === 'recompensa_paga').length,
                    })}
                  </p>
                </>
              ) : (
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Ficha</h2>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-slate-800"
              aria-label="Fechar"
            >
              <X size={18} />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            {carregando ? (
              <LoadingSpinner label="Carregando ficha..." />
            ) : erro ? (
              <p className="text-sm text-red-600">{erro}</p>
            ) : ficha && (
              <>
                <button onClick={() => setRegistrar(true)} className={CLASSE_BOTAO_PRIMARIO}>
                  <Plus size={15} /> Registrar abordagem
                </button>

                {ficha.pessoa.pode_editar_pagamento && (
                  <section className="space-y-2">
                    <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-slate-400">Dados para pagamento</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input className={CLASSE_INPUT} placeholder="CPF/CNPJ" value={cpf} onChange={e => setCpf(e.target.value)} />
                      <input className={CLASSE_INPUT} placeholder="Chave PIX" value={pix} onChange={e => setPix(e.target.value)} />
                    </div>
                    <button onClick={salvarPagamento} disabled={salvandoPag} className={CLASSE_BOTAO_SECUNDARIO}>
                      Salvar dados de pagamento
                    </button>
                  </section>
                )}

                <section className="space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-slate-400">Linha do tempo</h3>
                  {ficha.abordagens.length === 0 && espontaneas.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-slate-400">Nenhuma abordagem ainda.</p>
                  )}
                  {ficha.abordagens.map(a => (
                    <div key={a.id} className="border-l-2 border-primary/30 pl-3">
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        {formatarDataHora(a.abordado_em)} · {a.abordado_por_nome ?? '—'} · {ROTULO_CANAL[a.canal]}
                      </p>
                      <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">{ROTULO_RESULTADO[a.resultado]}</p>
                      {a.observacao && <p className="text-xs text-gray-600 dark:text-slate-300 italic">{a.observacao}</p>}
                      {(porAbordagem.get(a.id) ?? []).map(i => (
                        <LinhaIndicacao key={i.id} i={i} onAbrir={setNegAberta} />
                      ))}
                    </div>
                  ))}
                  {espontaneas.map(i => (
                    <div key={`e${i.id}`} className="border-l-2 border-secondary/40 pl-3">
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        {formatarDataHora(i.registrado_em)} · {i.registrado_por_nome ?? '—'} · indicação espontânea
                      </p>
                      <LinhaIndicacao i={i} onAbrir={setNegAberta} />
                    </div>
                  ))}
                </section>
              </>
            )}
          </div>
        </aside>
      </div>

      {registrar && ficha && (
        <RegistrarAbordagemModal
          gere={gere}
          pessoaInicial={{
            id: ficha.pessoa.id,
            nome: ficha.pessoa.nome,
            cpf_cnpj: ficha.pessoa.cpf_cnpj,
            whatsapp: ficha.pessoa.whatsapp,
            cliente: ficha.pessoa.cliente,
          }}
          onClose={() => setRegistrar(false)}
          onSalvo={() => {
            carregar();
            onMudou();
          }}
        />
      )}
      <NegotiationDetailModal
        negotiationId={negAberta}
        isOpen={negAberta != null}
        onClose={() => setNegAberta(null)}
        onUpdate={carregar}
      />
    </>
  );
};

export default FichaPessoaDrawer;
